import React, { FC, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'

import { Box } from '../UIKit/system'
import { darkBackground, lightGreyBackground } from '../../utils/styles'
import Container from '../container'
import TabsHeader from './tabs-header'
import TabsContent from './tabs-content'
import mq from '../../utils/mq'
import { useSearchParams } from '../../hooks/use-search-params'

const StyledTabs = styled(Box)<{ tabsTheme: SectionTabsProps['theme'] }>`
  ${({ tabsTheme }) => tabsTheme === 'dark' && darkBackground}
  ${({ tabsTheme }) => tabsTheme === 'light' && lightGreyBackground}
`

const StyledContainer = styled(Container)`
  ${mq.mobile(css`
    border-left-width: 0px;
    border-right-width: 0px;
  `)};
`

export interface SectionTabsProps {
  theme?: 'dark' | 'light';
  items: {
    name: string;
    searchId?: string;
  }[];
  productName?: string;
  current?: any;
  extended?: boolean;
  searchParamProp?: string;

  /**
   * Dismount inactive tab content
   * instead of hiding it with CSS.
   */
  dismountContent?: boolean;
}

const SectionTabs: FC<SectionTabsProps> = (props) => {
  const {
    theme = 'light',
    items,
    extended,
    productName,
    current,
    dismountContent,
    searchParamProp
  } = props
  const { search, searchParams } = useSearchParams()
  // Do not load the first tab in case it's loading third-party JS.
  const [activeIndex, setActiveIndex] = useState(-1)
  const tabsStyles = {
    dark: { tabsTheme: theme },
    light: { tabsTheme: theme, pt: [75, 100, 126], pb: [75, 95] }
  }

  useEffect(() => {
    if (!searchParamProp) {
      return setActiveIndex(0)
    }

    const tabIndex = items.findIndex(
      (item) => item.searchId === searchParams.get(searchParamProp)
    )

    if (tabIndex > 0) {
      return setActiveIndex(tabIndex)
    }

    setActiveIndex(0)
  }, [search])

  return (
    <StyledTabs {...tabsStyles[theme]}>
      <StyledContainer>
        <TabsHeader
          theme={theme}
          items={items}
          activeIndex={activeIndex}
          extended={extended}
          onSwitch={(index) => setActiveIndex(index)}
        />
        <TabsContent
          theme={theme}
          items={items}
          productName={productName}
          activeIndex={activeIndex}
          current={current}
          dismountContent={dismountContent}
        />
      </StyledContainer>
    </StyledTabs>
  )
}

export default SectionTabs
